"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.deriveSortState = exports.TableStateContext = exports.TableState = void 0;
const change_case_1 = require("change-case");
const mobx_1 = require("mobx");
const react_1 = __importDefault(require("react"));
const utils_1 = require("./utils");
const visitor_1 = require("./visitor");
const utils_2 = require("../../../utils");
const columns_1 = require("./columns");
/**
 * Stores the collapsed & selected state of rows.
 *
 * I.e. this implements "collapse parent" --> "hides children", and
 * "select parent" --> "select parent + children".
 *
 * There should be a single, stable `RowStateStore` instance per `GridTable`, so
 * that children don't have to re-render even as we incrementally add/remove rows
 * to the table (i.e. the top-level rows identity changes, but each row within it
 * may not).
 *
 * We use mobx ObservableSets/ObservableMaps to drive granular re-rendering of rows/cells
 * that need to change their toggle/select on/off in response to parent/child
 * changes.
 */
class TableState {
    /**
     * Creates the `RowState` for a given `GridTable`.
     */
    constructor() {
        // A set of just row ids, i.e. not row.kind+row.id
        this.collapsedRows = new mobx_1.ObservableSet();
        this.selectedRows = new mobx_1.ObservableMap();
        // Set of just row ids. Keeps track of which rows match the filter. Used to filter rows from `selectedIds`
        this.matchedRows = new mobx_1.ObservableSet();
        // The current list of rows, basically a useRef.current. Not reactive.
        this.rows = [];
        // Keeps track of the 'active' row, formatted `${row.kind}_${row.id}`
        this.activeRowId = undefined;
        // Keeps track of the 'active' cell, formatted `${row.kind}_${row.id}_${column.name}`
        this.activeCellId = undefined;
        // Provide some defaults to get the sort state to properly work.
        this.sort = {};
        // Non-reactive list of our columns
        this.columns = [];
        // An observable set of column ids to keep track of which columns are currently expanded
        this.expandedColumns = new mobx_1.ObservableSet();
        // An observable set of column ids to keep track of which columns are visible
        this.visibleColumns = new mobx_1.ObservableSet();
        this.visibleColumnsStorageKey = "";
        // Cache for already loaded expandable columns
        this.loadedColumns = new Map();
        // Make ourselves an observable so that mobx will do caching of .collapseIds so
        // that it'll be a stable identity for GridTable to useMemo against.
        (0, mobx_1.makeAutoObservable)(this, {
            // We only shallow observe rows so that:
            // a) we don't deeply/needlessly proxy-ize a large Apollo fragment cache, but
            // b) if rows changes, we re-run computeds like getSelectedRows that may need to see the
            // updated _contents_ of a given row, even if our other selected/matched row states don't change.
            // (as any b/c rows is private, so the mapped type doesn't see it)
            rows: mobx_1.observable.shallow,
            // Do not observe columns, expect this to be a non-reactive value for us to base our reactive values off of.
            columns: false,
        });
        // Whenever our `matchedRows` change (i.e. via filtering) then we need to re-derive header and parent rows' selected state.
        (0, mobx_1.reaction)(() => [...this.matchedRows.values()].sort(), () => {
            const map = new Map();
            map.set("header", deriveParentSelected(this.rows.flatMap((row) => this.setNestedSelectedStates(row, map))));
            // Merge the changes back into the selected rows state
            this.selectedRows.merge(map);
        }, { equals: mobx_1.comparer.shallow });
    }
    loadCollapse(persistCollapse, rows) {
        this.persistCollapse = persistCollapse;
        const sessionStorageIds = persistCollapse ? sessionStorage.getItem(persistCollapse) : null;
        // Initialize with our collapsed rows based on what is in sessionStorage. Otherwise check if any rows have been defined as collapsed
        const collapsedGridRowIds = sessionStorageIds ? JSON.parse(sessionStorageIds) : getCollapsedIdsFromRows(rows);
        // If we have some initial rows to collapse, then set the internal prop
        if (collapsedGridRowIds.length > 0) {
            this.collapsedRows.replace(collapsedGridRowIds);
            // If `persistCollapse` is set, but sessionStorageIds was not defined, then add them now.
            if (this.persistCollapse && !sessionStorageIds) {
                sessionStorage.setItem(this.persistCollapse, JSON.stringify(collapsedGridRowIds));
            }
        }
    }
    loadSelected(rows) {
        const selectedRows = rows.filter((row) => row.initSelected);
        // Initialize with selected rows as defined
        const map = new Map();
        selectedRows.forEach((row) => {
            map.set(row.id, "checked");
        });
        this.selectedRows.merge(map);
    }
    initSortState(sortConfig, columns) {
        var _a, _b;
        if (this.sortConfig) {
            return;
        }
        this.sortConfig = sortConfig;
        if ((sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.on) === "client") {
            const { initial, primary } = sortConfig;
            const primaryKey = primary === null || primary === void 0 ? void 0 : primary[0];
            const persistentSortData = primaryKey
                ? { persistent: { columnId: primaryKey, direction: (_a = primary === null || primary === void 0 ? void 0 : primary[1]) !== null && _a !== void 0 ? _a : utils_1.ASC } }
                : {};
            if (initial === undefined && "initial" in sortConfig) {
                // if explicitly set to `undefined`, then do not sort
                this.initialSortState = undefined;
            }
            else if (initial) {
                this.initialSortState = { current: { columnId: initial[0], direction: initial[1] }, ...persistentSortData };
            }
            else {
                // If no explicit sortState, assume 1st column ascending
                const firstSortableColumn = (_b = columns.find((c) => c.clientSideSort !== false)) === null || _b === void 0 ? void 0 : _b.id;
                this.initialSortState = firstSortableColumn
                    ? { current: { columnId: firstSortableColumn, direction: utils_1.ASC }, ...persistentSortData }
                    : undefined;
            }
        }
        else {
            this.initialSortState = (sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.value)
                ? { current: { columnId: sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.value[0], direction: sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.value[1] } }
                : undefined;
        }
        // Only change `this.sort` if `initialSortState` is defined.
        if (this.initialSortState) {
            this.sort = this.initialSortState;
        }
        this.onSort = (sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.on) === "server" ? sortConfig.onSort : undefined;
    }
    setSortKey(clickedColumnId) {
        var _a;
        if (this.sortConfig) {
            const newState = deriveSortState(this.sort, clickedColumnId, this.initialSortState);
            this.sort = newState !== null && newState !== void 0 ? newState : {};
            if (this.onSort) {
                const { columnId, direction } = (_a = newState === null || newState === void 0 ? void 0 : newState.current) !== null && _a !== void 0 ? _a : {};
                this.onSort(columnId, direction);
            }
        }
    }
    get sortState() {
        return this.sort.current ? this.sort : undefined;
    }
    // Updates the list of rows and regenerates the collapsedRows property if needed.
    setRows(rows) {
        // If the set of rows are different
        if (rows !== this.rows) {
            const currentCollapsedIds = this.collapsedIds;
            // Create a list of the (maybe) new rows that should be initially collapsed
            const maybeNewCollapsedRowIds = flattenRows(rows)
                .filter((r) => r.initCollapsed)
                .map((r) => r.id);
            // Check against local storage for collapsed state only if this is the first render of "data" (non-header or totals) rows.
            const checkLocalStorage = this.persistCollapse && !this.rows.some((r) => r.kind !== "totals" && r.kind !== "header");
            // If the list of collapsed rows are different, then determine which are net-new rows and should be added to the newCollapsedIds array
            if (currentCollapsedIds.length !== maybeNewCollapsedRowIds.length ||
                !currentCollapsedIds.every((id) => maybeNewCollapsedRowIds.includes(id))) {
                // Flatten out the existing rows to make checking for new rows easier
                const flattenedExistingIds = flattenRows(this.rows).map((r) => r.id);
                const newCollapsedIds = maybeNewCollapsedRowIds.filter((maybeNewRowId) => !flattenedExistingIds.includes(maybeNewRowId) &&
                    // Using `!` on `this.persistCollapse!` as `checkLocalStorage` ensures this.persistCollapse is truthy
                    (!checkLocalStorage || readCollapsedRowStorage(this.persistCollapse).includes(maybeNewRowId)));
                // If there are new rows that should be collapsed then update the collapsedRows arrays
                if (newCollapsedIds.length > 0) {
                    this.collapsedRows.replace(currentCollapsedIds.concat(newCollapsedIds));
                    // Also update our persistCollapse if set
                    if (this.persistCollapse) {
                        sessionStorage.setItem(this.persistCollapse, JSON.stringify(currentCollapsedIds.concat(newCollapsedIds)));
                    }
                }
            }
        }
        // Finally replace our existing list of rows
        this.rows = rows;
    }
    setColumns(columns, visibleColumnsStorageKey) {
        const isInitial = !this.columns || this.columns.length === 0;
        if (columns !== this.columns) {
            this.visibleColumnsStorageKey = visibleColumnsStorageKey !== null && visibleColumnsStorageKey !== void 0 ? visibleColumnsStorageKey : (0, change_case_1.camelCase)(columns.map((c) => c.id).join());
            this.visibleColumns.replace(readOrSetLocalVisibleColumnState(columns, this.visibleColumnsStorageKey));
            // Figure out which columns need to be expanded when rendering a new set of columns
            const localStorageColumns = this.persistCollapse ? readExpandedColumnsStorage(this.persistCollapse) : [];
            // On initial render the columns we start with is whatever is in local storage.
            // On any subsequent render, we start with an empty array.
            // (We'll add to this after figuring out which columns are new vs existing.)
            const columnIdsToExpand = isInitial ? localStorageColumns : [];
            // Create a list of all existing column ids. (We ignore the `initExpanded` property for existing columns)
            const existingColumnIds = this.columns.map((c) => c.id);
            // Add any columns to our array that are new columns that should be initially expanded.
            columnIdsToExpand.push(...columns.filter((c) => !existingColumnIds.includes(c.id) && c.initExpanded).map((c) => c.id));
            // Send the new array of columns along to be parsed and expanded.
            this.parseAndUpdateExpandedColumns(columns.filter((c) => columnIdsToExpand.includes(c.id)));
            this.columns = columns;
        }
    }
    /** Determines which columns to expand immediately vs async */
    async parseAndUpdateExpandedColumns(columnsToExpand) {
        // Separate out which columns need to be loaded async vs which can be loaded immediately.
        const [localColumnsToExpand, asyncColumnsToExpand] = columnsToExpand.reduce((acc, c) => {
            if ((0, utils_2.isFunction)(c.expandColumns)) {
                return [acc[0], acc[1].concat(c)];
            }
            return [acc[0].concat(c), acc[1]];
        }, [[], []]);
        // Handle all async expanding columns using a Promise.all.
        // This will allow the table to render immediately, then cause a rerender with the new columns
        if (asyncColumnsToExpand.length > 0) {
            // Note: Not using a Promise.all because there seems to be a bug in Apollo with applying TypePolicies when using Promise.all.
            // TODO: Update comment with Apollo issue link.
            // Promise.all(asyncColumnsToExpand.map(async (c) => await this.loadExpandedColumns(c))).then(() =>
            //   this.updateExpandedColumns(asyncColumnsToExpand),
            // );
            // Instead, doing each async request in sequence for now.
            for await (const column of asyncColumnsToExpand) {
                await this.loadExpandedColumns(column);
            }
            this.updateExpandedColumns(asyncColumnsToExpand);
        }
        // For local columns, we skip the Promise in order to have the correct state on the initial load.
        if (localColumnsToExpand.length > 0) {
            this.updateExpandedColumns(localColumnsToExpand);
        }
    }
    /** Updates the state of which columns are expanded */
    updateExpandedColumns(newColumns) {
        const newColumnIds = newColumns.map((c) => c.id);
        // If there is a difference between list of current expanded columns vs list we just created, then replace
        const isDifferent = newColumnIds.length !== this.expandedColumnIds.length ||
            !this.expandedColumnIds.every((c) => newColumnIds.includes(c));
        if (isDifferent) {
            // Note: `this.expandedColumns` is a Set, so it will take care of dedupe-ing.
            this.expandedColumns.replace([...this.expandedColumnIds, ...newColumnIds]);
            // Update session storage if necessary.
            if (this.persistCollapse) {
                sessionStorage.setItem(getColumnStorageKey(this.persistCollapse), JSON.stringify([...this.expandedColumns]));
            }
        }
    }
    // load and trigger column to be expanded
    async loadExpandedColumns(column) {
        // if we don't have anything in our cache and our expanded columns are a function
        if (!this.loadedColumns.has(column.id) && (0, utils_2.isFunction)(column.expandColumns)) {
            // set our result to the function call of expandColumns
            const result = await column.expandColumns();
            // once we have the loaded columns, add result to local cache
            this.loadedColumns.set(column.id, (0, columns_1.assignDefaultColumnIds)(result));
        }
    }
    // if there is a promise, then grab the already loaded expandable columns from the cache, if not then return the expandedColumns property
    getExpandedColumns(column) {
        var _a, _b;
        return (0, utils_2.isFunction)(column.expandColumns) ? (_a = this.loadedColumns.get(column.id)) !== null && _a !== void 0 ? _a : [] : (_b = column.expandColumns) !== null && _b !== void 0 ? _b : [];
    }
    setVisibleColumns(ids) {
        // If we have a new visible columns, then we need to check if some need to be initially expanded when made visible
        if (ids.length > this.visibleColumnIds.length) {
            // Get a list of columns that are just now being made visible.
            const newlyVisibleIds = ids.filter((id) => !this.visibleColumnIds.includes(id));
            // Figure out if any of these newly visible columns needs to be initially expanded.
            const columnsToExpand = this.columns.filter((c) => newlyVisibleIds.includes(c.id) && c.initExpanded);
            this.parseAndUpdateExpandedColumns(columnsToExpand);
        }
        sessionStorage.setItem(this.visibleColumnsStorageKey, JSON.stringify(ids));
        this.visibleColumns.replace(ids);
    }
    get visibleColumnIds() {
        return [...this.visibleColumns.values()];
    }
    get expandedColumnIds() {
        return [...this.expandedColumns.values()];
    }
    toggleExpandedColumn(columnId) {
        if (this.expandedColumns.has(columnId)) {
            this.expandedColumns.delete(columnId);
        }
        else {
            this.expandedColumns.add(columnId);
        }
        if (this.persistCollapse) {
            sessionStorage.setItem(getColumnStorageKey(this.persistCollapse), JSON.stringify(this.expandedColumnIds));
        }
    }
    setMatchedRows(rowIds) {
        // ObservableSet doesn't seem to do a `diff` inside `replace` before firing
        // observers/reactions that watch it, which can lead to render loops with the
        // application page is observing `GridTableApi.getSelectedRows`, and merely
        // the act of rendering GridTable (w/o row changes) causes it's `useComputed`
        // to be triggered.
        if (!mobx_1.comparer.shallow(rowIds, [...this.matchedRows.values()])) {
            this.matchedRows.replace(rowIds);
        }
    }
    get selectedIds() {
        // Return only ids that are fully checked, i.e. not partial
        const ids = [...this.selectedRows.entries()]
            .filter(([id, v]) => this.matchedRows.has(id) && v === "checked")
            .map(([k]) => k);
        // Hide our header marker
        const headerIndex = ids.indexOf("header");
        if (headerIndex > -1) {
            ids.splice(headerIndex, 1);
        }
        return ids;
    }
    // Should be called in an Observer/useComputed to trigger re-renders
    getSelected(id) {
        // We may not have every row in here, i.e. on 1st page load or after clicking here, so assume unchecked
        return this.selectedRows.get(id) || "unchecked";
    }
    selectRow(id, selected) {
        if (id === "header") {
            // Select/unselect all has special behavior
            if (selected) {
                // Just mash the header + all rows + children as selected
                const map = new Map();
                map.set("header", "checked");
                (0, visitor_1.visit)(this.rows, (row) => this.matchedRows.has(row.id) && row.kind !== "totals" && map.set(row.id, "checked"));
                this.selectedRows.replace(map);
            }
            else {
                // Similarly "unmash" all rows + children.
                this.selectedRows.clear();
            }
        }
        else {
            // This is the regular/non-header behavior to just add/remove the individual row id,
            // plus percolate the change down-to-child + up-to-parents.
            // Find the clicked on row
            const curr = findRow(this.rows, id);
            if (!curr) {
                return;
            }
            // Everything here & down is deterministically on/off
            const map = new Map();
            (0, visitor_1.visit)([curr.row], (row) => this.matchedRows.has(row.id) && map.set(row.id, selected ? "checked" : "unchecked"));
            // Now walk up the parents and see if they are now-all-checked/now-all-unchecked/some-of-each
            for (const parent of [...curr.parents].reverse()) {
                // Only derive selected state of the parent row if `inferSelectedState` is not `false`
                if (parent.children && parent.inferSelectedState !== false) {
                    map.set(parent.id, deriveParentSelected(this.getMatchedChildrenStates(parent.children, map)));
                }
            }
            // And do the header + top-level "children" as a final one-off
            map.set("header", deriveParentSelected(this.getMatchedChildrenStates(this.rows, map)));
            this.selectedRows.merge(map);
        }
    }
    get collapsedIds() {
        return [...this.collapsedRows.values()];
    }
    // Should be called in an Observer/useComputed to trigger re-renders
    isCollapsed(id) {
        return this.collapsedRows.has(id);
    }
    toggleCollapsed(id) {
        const collapsedIds = [...this.collapsedRows.values()];
        // We have different behavior when going from expand/collapse all.
        if (id === "header") {
            const isAllCollapsed = collapsedIds.includes("header");
            if (isAllCollapsed) {
                // Expand all means keep `collapsedIds` empty
                collapsedIds.splice(0, collapsedIds.length);
            }
            else {
                // Otherwise push `header` to the list as a hint that we're in the collapsed-all state
                collapsedIds.push("header");
                // Find all non-leaf rows so that toggling "all collapsed" -> "all not collapsed" opens
                // the parent rows of any level.
                const parentIds = new Set();
                const todo = [...this.rows];
                while (todo.length > 0) {
                    const r = todo.pop();
                    if (r.children) {
                        parentIds.add(r.id);
                        todo.push(...r.children);
                    }
                }
                // And then mark all parent rows as collapsed.
                collapsedIds.push(...parentIds);
            }
        }
        else {
            // This is the regular/non-header behavior to just add/remove the individual row id
            const i = collapsedIds.indexOf(id);
            if (i === -1) {
                collapsedIds.push(id);
            }
            else {
                collapsedIds.splice(i, 1);
            }
            // If all rows have been expanded individually, but the 'header' was collapsed, then remove the header from the collapsedIds so it reverts to the expanded state
            if (collapsedIds.length === 1 && collapsedIds[0] === "header") {
                collapsedIds.splice(0, 1);
            }
            else {
                // If every top level child has been collapsed, then push "header" into the array to be considered collapsed as well.
                if (this.rows.every((maybeParent) => (maybeParent.children ? collapsedIds.includes(maybeParent.id) : true))) {
                    collapsedIds.push("header");
                }
            }
        }
        this.collapsedRows.replace(collapsedIds);
        if (this.persistCollapse) {
            sessionStorage.setItem(this.persistCollapse, JSON.stringify(collapsedIds));
        }
    }
    getMatchedChildrenStates(children, map) {
        const respectedChildren = children.flatMap(getChildrenForDerivingSelectState);
        return respectedChildren
            .filter((row) => row.id !== "header" && this.matchedRows.has(row.id))
            .map((row) => map.get(row.id) || this.getSelected(row.id));
    }
    // Recursively traverse through rows to determine selected state of parent rows based on children
    setNestedSelectedStates(row, map) {
        if (this.matchedRows.has(row.id)) {
            // do not derive selected state if there are no children, or if `inferSelectedState` is set to false
            if (!row.children || row.inferSelectedState === false) {
                return [this.getSelected(row.id)];
            }
            const childrenSelectedStates = row.children.flatMap((rc) => this.setNestedSelectedStates(rc, map));
            const parentState = deriveParentSelected(childrenSelectedStates);
            map.set(row.id, parentState);
            return [parentState];
        }
        return [];
    }
}
exports.TableState = TableState;
/** Returns the child rows needed for deriving the selected state of a parent/group row */
function getChildrenForDerivingSelectState(row) {
    if (row.children && row.inferSelectedState === false) {
        return [row, ...row.children.flatMap(getChildrenForDerivingSelectState)];
    }
    return [row];
}
/** Provides a context for rows to access their table's `TableState`. */
exports.TableStateContext = react_1.default.createContext({
    get tableState() {
        throw new Error("No TableStateContext provider");
    },
});
function readCollapsedRowStorage(persistCollapse) {
    const collapsedGridRowIds = sessionStorage.getItem(persistCollapse);
    return collapsedGridRowIds ? JSON.parse(collapsedGridRowIds) : [];
}
function readExpandedColumnsStorage(persistCollapse) {
    const expandedGridColumnIds = sessionStorage.getItem(getColumnStorageKey(persistCollapse));
    return expandedGridColumnIds ? JSON.parse(expandedGridColumnIds) : [];
}
// Get the columns that are already in the visible state so we keep them toggled.
function readOrSetLocalVisibleColumnState(columns, storageKey) {
    const storageValue = sessionStorage.getItem(storageKey);
    if (storageValue) {
        return JSON.parse(storageValue);
    }
    const visibleColumnIds = columns.filter((c) => c.initVisible || !c.canHide).map((c) => c.id);
    sessionStorage.setItem(storageKey, JSON.stringify(visibleColumnIds));
    return visibleColumnIds;
}
/** Finds a row by id, and returns it + any parents. */
function findRow(rows, id) {
    // This is technically an array of "maybe FoundRow"
    const todo = rows.map((row) => ({ row, parents: [] }));
    while (todo.length > 0) {
        const curr = todo.pop();
        if (curr.row.id === id) {
            return curr;
        }
        else if (curr.row.children) {
            // Search our children and pass along us as the parent
            todo.push(...curr.row.children.map((child) => ({ row: child, parents: [...curr.parents, curr.row] })));
        }
    }
    return undefined;
}
function deriveParentSelected(children) {
    const allChecked = children.every((child) => child === "checked");
    const allUnchecked = children.every((child) => child === "unchecked");
    return children.length === 0 ? "unchecked" : allChecked ? "checked" : allUnchecked ? "unchecked" : "partial";
}
function getCollapsedIdsFromRows(rows) {
    return rows.reduce((acc, r) => {
        if (r.initCollapsed) {
            acc.push(r.id);
        }
        if (r.children) {
            acc.push(...getCollapsedIdsFromRows(r.children));
        }
        return acc;
    }, []);
}
function flattenRows(rows) {
    const childRows = rows.flatMap((r) => (r.children ? flattenRows(r.children) : []));
    return [...rows, ...childRows];
}
function getColumnStorageKey(storageKey) {
    return `expandedColumn_${storageKey}`;
}
// Exported for testing purposes
function deriveSortState(currentSortState, clickedKey, initialSortState) {
    var _a;
    // If the current sort state is not defined then sort ASC on the clicked key.
    if (!currentSortState.current) {
        return { ...initialSortState, current: { columnId: clickedKey, direction: utils_1.ASC } };
    }
    const { current: { columnId: currentKey, direction: currentDirection }, } = currentSortState;
    // If clicking a new column, then sort ASC on the clicked key
    if (clickedKey !== currentKey) {
        return { ...initialSortState, current: { columnId: clickedKey, direction: utils_1.ASC } };
    }
    // If there is an `initialSortState` and we're clicking on that same key, then flip the sort.
    // Handles cases where the initial sort is DESC so that we can allow for DESC to ASC sorting.
    if (initialSortState && ((_a = initialSortState.current) === null || _a === void 0 ? void 0 : _a.columnId) === clickedKey) {
        return {
            ...initialSortState,
            current: { columnId: clickedKey, direction: currentDirection === utils_1.ASC ? utils_1.DESC : utils_1.ASC },
        };
    }
    // Otherwise when clicking the current column, toggle through sort states
    if (currentDirection === utils_1.ASC) {
        // if ASC -> go to desc
        return { ...initialSortState, current: { columnId: clickedKey, direction: utils_1.DESC } };
    }
    // Else, direction is already DESC, so revert to original sort value.
    return initialSortState;
}
exports.deriveSortState = deriveSortState;
